import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AddCourseToTrackModal from "../../../../../components/Modals/AddCourseToTrackModal";
import ConfirmationModal from "../../../../../components/Modals/ConfirmationModal";
import AdminLearningPathdetailsCard from "../../../../../components/cards/AdminLearningPathdetailsCard";
import {
  useAddCourseToLearningTrackMutation,
  useRemoveCourseFromLearningTrackMutation,
} from "../../../../../services/organization/adminLearningTrack";
import EmptyState from "../../../../EmptyState";
import { useTranslation } from "react-i18next";

export default function Courses({ learningTrack }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [courseToRemove, setCourseToRemove] = useState(null);
  const { t } = useTranslation(["common", "learningPaths"]);

  const [addCourseToLearningTrack] = useAddCourseToLearningTrackMutation();
  const [removeCourseFromLearningTrack] =
    useRemoveCourseFromLearningTrackMutation();
  const learningTrackCourseIdsSet = new Set(
    learningTrack?.courses?.map((course) => course.id),
  );

  async function handleAddCourse(courseData) {
    try {
      const coursesToAdd = Array.isArray(courseData)
        ? courseData
        : [courseData];
      const courseIds = coursesToAdd.map((c) => c.id);

      await addCourseToLearningTrack({
        learningTrackId: learningTrack.id,
        body: { course_ids: courseIds },
      }).unwrap();
      toast.success(t("learningPaths:courseAddedSuccess"));
      setShowAddModal(false);
    } catch (e) {
      console.error(e);
      toast.error(t("common:somethingWentWrong"));
    }
  }

  useEffect(() => {
    const el = document.getElementById("remove-course-from-track-confirmation");
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (courseToRemove) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [courseToRemove]);

  useEffect(() => {
    const el = document.getElementById("remove-course-from-track-confirmation");
    if (!el) return;
    const handleHidden = () => setCourseToRemove(null);
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, []);

  async function handleConfirmRemoveCourse() {
    if (!courseToRemove?.id || !learningTrack?.id) return;
    try {
      await removeCourseFromLearningTrack({
        learningTrackId: learningTrack.id,
        body: { course_ids: [courseToRemove.id] },
      }).unwrap();
      toast.success(t("learningPaths:coursesRemovedFromTrackSuccess"));
      setCourseToRemove(null);
    } catch (e) {
      toast.error(t("common:somethingWentWrong"));
    }
  }

  return (
    <div id="nav-profile" role="tabpanel" aria-labelledby="nav-profile-tab">
      <div className="container-fluid">
        <div className="row p-4">
          <div className="admin-learning-path-details__courses-toolbar d-flex align-center flex-wrap">
            <div className="admin-learning-path-details__courses-heading">
              <h1 className="font_20 font-extrabold mb-0">
                {learningTrack.courses.length}{" "}
                {t("learningPaths:coursesInTracks")}
              </h1>
            </div>

            {!learningTrack?.archived && (
              <div className="ms-auto d-flex gap-2 flex-wrap admin-learning-path-details__courses-actions">
                <button
                  type="button"
                  className="btn btn-main-color font-15"
                  onClick={() => setShowAddModal(true)}
                >
                  <span className="fas fa-plus fa-fw me-2"></span>
                  {t("learningPaths:addCourseToTrack")}
                </button>
              </div>
            )}
            {showAddModal && (
              <AddCourseToTrackModal
                isOpened={showAddModal}
                onClose={() => setShowAddModal(false)}
                onAddCourse={handleAddCourse}
                existingCourseIds={learningTrackCourseIdsSet}
              />
            )}
          </div>
          <ul className="list-progressbar-cards mt-3">
            {learningTrack.courses.length > 0 ? (
              learningTrack.courses.map((course, index) => (
                <li key={`course-${course.id}`}>
                  <hr className="border-dashed" />
                  <span className="list-progressbar-cards-state">
                    {index + 1}
                  </span>
                  <AdminLearningPathdetailsCard
                    listDetails
                    course={course}
                    courseLabel={course.topics[0]?.title}
                    courseLabelColor={course.topics[0]?.color || "#C15258"}
                    title={course.title}
                    tutorName={course.tutor?.name}
                    courseImage={course.image_url}
                    onRemove={
                      learningTrack?.archived
                        ? undefined
                        : () => setCourseToRemove(course)
                    }
                  />
                </li>
              ))
            ) : (
              <EmptyState
                iconComponent={<i className="far fa-play-circle" />}
                title={t("emptyStates.courses")}
                emptyStateText={t("learningPaths:emptyCourseTrack")}
              />
            )}
          </ul>
        </div>
      </div>
      <ConfirmationModal
        modalId="remove-course-from-track-confirmation"
        isOpened={Boolean(courseToRemove)}
        onClose={() => setCourseToRemove(null)}
        confirmationMessage={t("learningPaths:confirmRemoveCourseFromTrack", {
          title: courseToRemove?.title || "",
        })}
        handleConfirmation={handleConfirmRemoveCourse}
      />
    </div>
  );
}
